import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors the response of POST /evaluation/results/resultsByUser
// (service-evaluation, SuperAdmin only): used here to find out whether a
// lesson's embedded quiz was taken - that never goes through
// Progress.evaluationsPassed (EvaluationResultController.register writes
// straight to EvaluationResult), so it's the only way to know.
export interface IProgressAuditEvaluationResult {
  userId: string;
  evaluationId: string;
  result: number;
}

interface ProgressAuditEvaluationResultsState {
  list: IProgressAuditEvaluationResult[];
  status: FetchStatus;
}

const initialState: ProgressAuditEvaluationResultsState = {
  list: [],
  status: FetchStatus.Idle,
};

export const progressAuditEvaluationResultsSlice = createSlice({
  name: "progressAuditEvaluationResults",
  initialState,
  reducers: {
    fetching: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fetched: (state, action: PayloadAction<{ results: IProgressAuditEvaluationResult[] }>) => {
      state.list = action.payload?.results || [];
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.list = [];
      state.status = FetchStatus.Error;
    },
    clear: (state) => {
      state.list = [];
      state.status = FetchStatus.Idle;
    },
  },
});

export const {
  fetching,
  fetched,
  error,
  clear: clearProgressAuditEvaluationResults,
} = progressAuditEvaluationResultsSlice.actions;

export const fetchProgressAuditEvaluationResults = (username: string, evaluationIds: string[]) => (
  dispatch: Dispatch<object>
) => {
  return dispatch(
    apiActionCreator({
      endpoint: "/evaluation/results/resultsByUser",
      types: {
        requestType: fetching,
        successTypes: [
          {
            actionOrCreator: fetched,
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
          },
        ],
      },
      data: [{ username, evaluationIds }],
      method: HttpMethods.POST,
      microservice: Microservices.EVALUATION_SERVICE,
      authenticated: true,
    })
  );
};

export const selectProgressAuditEvaluationResults = (state: RootState) =>
  state.progressAuditEvaluationResults.list || [];
export const selectIsFetchingProgressAuditEvaluationResults = (state: RootState) =>
  state.progressAuditEvaluationResults.status === FetchStatus.Fetching;

export default progressAuditEvaluationResultsSlice.reducer;
